import { NumberUtil } from "@mcleod/core";
import { DesignerInterface } from "../../../../base/DesignerInterface";
import { Label } from "../../../label/Label";
import { Panel } from "../../../panel/Panel";
import { PanelProps } from "../../../panel/PanelProps";
import { Table } from "../../Table";
import { RowCountLabel } from "./RowCountLabel";
import { DataSourceExecutionEvent } from "../../../../events/DataSourceExecutionEvent";
import { DataSource, DataSourceAction } from "../../../../databinding/DataSource";

enum InnerIds {
    LABEL_ROW_COUNT = "labelRowCount",
    LABEL_TOTAL_COUNT = "labelTotalCount"
}

export class RowCountPanel extends Panel {
    private table: Table;
    private _labelRowCount: RowCountLabel;
    private labelTotalCount: Label;
    private _tableDataSource: DataSource;
    private baselineActualRowCount: number;
    private baselineLoadedRowCount: number;

    constructor(table: Table, props?: Partial<PanelProps>) {
        super({ margin: 0, padding: 0, ...props });
        this.table = table;
        this.createLabelRowCount();
        this.createLabelTotalCount();
    }


    override syncInnerComponentIds() {
        this.setInnerComponentId(this.labelRowCount, InnerIds.LABEL_ROW_COUNT);
        this.setInnerComponentId(this.labelTotalCount, InnerIds.LABEL_TOTAL_COUNT);
    }

    refresh() {
        this.tableDataSource = this.table.dataSource;
        this.labelRowCount.refresh();
        this.refreshLabelTotalCount();
    }

    private set tableDataSource(value: DataSource) {
        if (this._tableDataSource !== value) {
            if (this._tableDataSource) {
                this._tableDataSource.removeAfterExecutionListener(this.doAfterDataSourceSearch);
            }
            this._tableDataSource = value;
            value?.addAfterExecutionListener(this.doAfterDataSourceSearch);

            this.baselineActualRowCount = value?.actualRowCount ?? 0;
            this.baselineLoadedRowCount = value?.rowCount ?? 0;
        }
    }

    private get tableDataSource(): DataSource {
        return this._tableDataSource;
    }

    private doAfterDataSourceSearch = (event: DataSourceExecutionEvent): void => {
        if (event.getAction() !== DataSourceAction.SEARCH) return;
        // DataSource.actualRowCount is a server snapshot from search.
        // Since rows can be added/removed through many code paths, derive displayed total
        // from a captured baseline (search actual + loaded count) instead of modifying actualRowCount.
        this.baselineActualRowCount = event.dataSource.actualRowCount;
        this.baselineLoadedRowCount = event.dataSource.rowCount;
        this.refreshLabelTotalCount();
    };

    private refreshLabelTotalCount() {
        // Only show total count if search results were truncated and we have a valid actualRowCount from the server
        if (this._designer != null ||
            this.tableDataSource?.exceededMaxSearchResults !== true ||
            !NumberUtil.isPositive(this.tableDataSource?.rowCount) ||
            !NumberUtil.isPositive(this.tableDataSource?.actualRowCount)) {
            this.remove(this.labelTotalCount, false);
            return;
        }

        const actualRowCount = this.calculateActualRowCount();
        if (NumberUtil.isPositive(actualRowCount)) {
            this.labelTotalCount.text = `${NumberUtil.numberWithCommas(actualRowCount)} Total`;
            this.addIfNotPresent(this.labelTotalCount);
        } else {
            this.remove(this.labelTotalCount, false);
        }
    }

    private calculateActualRowCount(): number {
        const currentRowCount = this.tableDataSource?.rowCount ?? 0;
        return this.baselineActualRowCount + (currentRowCount - this.baselineLoadedRowCount);
    }

    private createLabelRowCount() {
        this.labelRowCount = new RowCountLabel(this.table, {
            id: this.createInnerComponentId(InnerIds.LABEL_ROW_COUNT),
            padding: 0,
            height: 20
        });

        this.labelRowCount.textSupplier = (table) => {
            const filteredCount = table.filteredRows?.length ?? 0;
            const loadedCount = this.tableDataSource?.rowCount ?? 0;
            const exceeded = this.tableDataSource?.exceededMaxSearchResults === true;
            if (!exceeded || loadedCount === 0 || filteredCount === loadedCount) return null;
            return `${NumberUtil.numberWithCommas(filteredCount)} of ${NumberUtil.numberWithCommas(loadedCount)} Results`;
        };
        this.add(this.labelRowCount);
    }

    get labelRowCount(): RowCountLabel {
        return this._labelRowCount;
    }

    private set labelRowCount(value: RowCountLabel) {
        this._labelRowCount = value;
    }

    private createLabelTotalCount() {
        this.labelTotalCount = new Label({
            id: this.createInnerComponentId(InnerIds.LABEL_TOTAL_COUNT),
            padding: 0,
            height: 16
        });
        this.labelTotalCount.applyTheme(RowCountLabel.themeObjectRowCount, true);
        this.labelTotalCount.fontSize = "small";
    }

    override get _designer(): DesignerInterface {
        return super._designer;
    }

    override set _designer(value: DesignerInterface) {
        super._designer = value;
        this.labelRowCount._designer = value;
        this.labelTotalCount._designer = value;
    }
}
